<template>
    <div
        class="tw-flex tw-justify-end sm:tw-justify-between tw-flex-col sm:tw-flex-row tw-items-start sm:tw-items-center font-body"
        id="page-filter-section"
    >
        <label
            class="tw-text-2xl tw-text-primary tw-font-semibold tw-flex tw-items-center tw-gap-4"
        >
            {{ capitalizeFirstLetter(title ?? "") }}
            <span
                class="tw-text-customGray-300 tw-text-sm"
                v-if="totalProducts"
                >{{
                    `${totalProducts === 1 ? $t("home.total_product", { amount: totalProducts }) : $t("home.total_products", { amount: totalProducts })}`
                }}</span
            >
        </label>

        <slot name="search"></slot>

        <div
            class="tw-flex tw-flex-row-reverse tw-justify-center lg:tw-justify-end tw-gap-3 tw-py-2"
        >
            <Select
                dense
                v-model="sortByProxy"
                :options="sortOptions"
                option-label="label"
                option-value="value"
                hide-dropdown-icon="expand_less"
                dropdown-icon="expand_more"
                borderless
                class="tw-text-xs lg:tw-text-base tw-text-customGray-500 tw-self-center tw-border-x tw-border-y tw-border-customGray-100 tw-px-4 !tw-py-0 !tw-w-max"
                @update:model-value="onSortBySelected"
            >
                <template #selected>
                    <span
                        class="tw-text-customGray-500 tw-text-xs md:tw-text-base"
                        >{{
                            sortOptions.filter(
                                (s) => s.value === sortByProxy
                            )[0].label
                        }}</span
                    >
                </template>
            </Select>
            <Select
                class="tw-ml-1 tw-text-xs lg:tw-text-base tw-text-customGray-500 tw-self-center tw-text-center tw-min-w-[20px] tw-border-x tw-border-y tw-border-customGray-100 tw-px-4 !tw-py-0"
                dense
                borderless
                hide-dropdown-icon="expand_less"
                dropdown-icon="expand_more"
                v-model="perPageProxy"
                :options="perPageOptions"
                option-label="title"
                option-value="value"
                @update:model-value="onPerPageSelected"
                emit-value
            >
                <template #selected>
                    <span
                        class="tw-text-customGray-500 tw-text-xs md:tw-text-base"
                        >{{ perPageProxy }}</span
                    >
                </template>
            </Select>
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue"
import type { View } from "~/models/View"
import { useI18n } from "vue-i18n"
import type { LenghtAwarePagination } from "~/models/Pagination"
import { useQuasar } from "quasar"
import type { Product } from "~/models/Product"
import {
    capitalizeFirstLetter,
    formattedPerPageOptions,
} from "~/common/helpers"

const $q = useQuasar()
const props = defineProps<{
    title: string | null
    pagination: LenghtAwarePagination<Product>
    view: View
    views: View[]
    totalProducts: number
}>()

const emits = defineEmits<{
    (e: "update:perPage", val: number): void
    (e: "update:pagination", val: Map<string, any>): void
    (e: "update:view", val: View): void
}>()

const i18n = useI18n()

$q.screen.setSizes({ sm: 640, md: 768, lg: 1024, xl: 1536 })

const sortOptions = ref([
    {
        label: i18n.t("fields.outstanding"),
        value: "priority",
        sortBy: "priority",
        descending: false,
    },
    {
        label: i18n.t("fields.name"),
        value: "name",
        sortBy: "name",
        descending: false,
    },
    {
        label: i18n.t("fields.latest"),
        value: "latest",
        sortBy: "created_at",
        descending: true,
    },
    {
        label: i18n.t("fields.price_up"),
        value: "price_up",
        sortBy: "sale_price",
        descending: false,
    },
    {
        label: i18n.t("fields.price_down"),
        value: "price_down",
        sortBy: "sale_price",
        descending: true,
    },
])

const perPageProxy = computed<number>(
    () => props.pagination.per_page ?? props.totalProducts
)
const total = computed(() => props.pagination.total ?? 16)
const sortByProxy = ref("priority")
const perPageOptions = computed(() =>
    formattedPerPageOptions(total.value, [
        { title: perPageProxy.value.toString(), value: perPageProxy.value },
    ])
)

const onSortBySelected = (sBP: string) => {
    const selected = sortOptions.value.find((s) => s.value == sBP)

    if (selected) {
        let map = new Map()
        map.set("sort_by", selected.sortBy)
        map.set("descending", selected.descending)

        emits("update:pagination", map)
    }
}

const onPerPageSelected = (pps: number) => {
    let map = new Map()
    map.set("per_page", pps)
    emits("update:pagination", map)
}
</script>
